'use client';

import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import type { MapContainerProps, TileLayerProps } from 'react-leaflet';
import { Button } from '@/shared/ui/button';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';
import './driver-tracking-modal.scss';

const MapContainer = dynamic<MapContainerProps>(
    () => import('react-leaflet').then((mod) => mod.MapContainer),
    {
        ssr: false,
    },
);
const TileLayer = dynamic<TileLayerProps>(() => import('react-leaflet').then((mod) => mod.TileLayer), {
    ssr: false,
});

type Props = ModalProps & {
    children?: ReactNode;
};

type DeliveryTrackingModalContextValue = {
    onClose?: ModalProps['onClose'];
};

const DeliveryTrackingModalContext = createContext<DeliveryTrackingModalContextValue | null>(null);

/** Provider that only provides context and renders children (Modal prefab). */
export const DeliveryTrackingModalProvider = ({ children, ...props }: Props) => {
    return (
        <DeliveryTrackingModalContext.Provider value={{ onClose: props.onClose }}>
            {children}
        </DeliveryTrackingModalContext.Provider>
    );
};

export const DeliveryTrackingModalWrapper = ({ children, ...props }: Props) => {
    return (
        <DeliveryTrackingModalContext.Provider value={{ onClose: props.onClose }}>
            <Modal
                {...props}
                wrapperProps={{
                    containerProps: { className: 'driver-tracking-modal__wrapper' },
                    scrollerProps: { className: 'driver-tracking-modal__scroller' },
                    'data-testid': 'driver-tracking-modal',
                }}
                closeOnOverlayClick={false}
            >
                {children}
            </Modal>
        </DeliveryTrackingModalContext.Provider>
    );
};

const DeliveryTrackingModalMap = ({ className, ...props }: MapContainerProps) => {
    return (
        <MapContainer className={className} center={[51.505, -0.09]} zoom={13} zoomControl={false} {...props}>
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
        </MapContainer>
    );
};

type TitleProps = ComponentProps<typeof Layout>;
const DeliveryTrackingModalTitle = ({ media, ...props }: TitleProps) => {
    const t = useTranslations();
    return (
        <Layout element="p" media={media} data-testid="driver-tracking-modal-title" {...props}>
            {t('DRIVER_TRACKING_MODAL.TITLE')}
        </Layout>
    );
};

type DescriptionProps = ComponentProps<typeof Layout>;
const DeliveryTrackingModalDescription = ({ media, ...props }: DescriptionProps) => {
    const t = useTranslations();
    return (
        <Layout element="p" media={media} data-testid="driver-tracking-modal-description" {...props}>
            {t('DRIVER_TRACKING_MODAL.DESCRIPTION', { time: 5 })}
        </Layout>
    );
};

type CloseButtonProps = ComponentProps<typeof Button>;
const DeliveryTrackingModalCloseButton = ({ onClick, ...props }: CloseButtonProps) => {
    const t = useTranslations();
    const context = useContext(DeliveryTrackingModalContext);
    const onClose = context?.onClose;
    return (
        <Layout
            element={Button}
            onClick={(e) => {
                onClick?.(e);
                onClose?.();
            }}
            data-testid="driver-tracking-modal-button"
            {...props}
        >
            {t('DRIVER_TRACKING_MODAL.BUTTON')}
        </Layout>
    );
};

export { DeliveryTrackingModalWrapper as DeliveryTrackingModal };

export {
    DeliveryTrackingModalWrapper as Wrapper,
    DeliveryTrackingModalProvider as Provider,
    DeliveryTrackingModalMap as Map,
    DeliveryTrackingModalTitle as Title,
    DeliveryTrackingModalDescription as Description,
    DeliveryTrackingModalCloseButton as CloseButton,
};
